// Created: 2026-08-14
// Updated: 2026-08-14
//
// NOTIFICATION mutations (react-query-notifications, Req 5).
//
// Inherited convention (slices 1–5): the MUTATION OWNS THE WRITE — it performs the call through the
// existing transport AND owns the cache invalidation, so the write+refresh unit lives in one place
// instead of being duplicated across the components that trigger it. Here that replaces two copies of
// the same hand-rolled optimism: `NotificationCenter`'s sweep over every cached category plus its
// `Math.max(0, prev.count - 1)` counter arithmetic, and `NotificationBell`'s private
// `setAllNotifications(prev => prev.map(...))`.
//
// ─── THE INVALIDATION MATRIX (design § D4) ─────────────────────────────────────────────────────────
//
// Derived from what each HANDLER writes, not from what the calling component happens to display:
//
//   mark-read                     → list('all') + list(category) + unreadCount()
//   delete, row was UNREAD        → list('all') + list(category) + unreadCount()
//   delete, row was already READ  → list('all') + list(category)            ← NO unreadCount()
//
// And, in every row, explicitly NOT: any sibling category, and no other section's key.
//
// EVIDENCE, row by row:
//
//  * mark-read — `lambda-secure/src/services/notifications.ts:625-654` does TWO writes: an
//    `UpdateCommand` setting `read = true` on `user#{id}` / `notification#{category}#{paddedMs}#{id}`,
//    and — only when `ReturnValues: 'ALL_OLD'` reports `read === false` — an
//    `ADD unread_count :negOne, #cat :negOne` on `user#{id}` / `notification_unread_counter`. The row is
//    in the `all` list and its own category's list; the counter IS the unread-count entry. Three keys,
//    all three moved. The conditional decrement does not soften the count invalidation: the client only
//    fires mark-read when `!notification.read` (Req 5.10), and refetching an unchanged count is harmless
//    while skipping a changed one is not.
//
//  * SIBLING CATEGORIES ARE UNREACHABLE — `services/notifications.ts:124-127` builds the SK as
//    `notification#{category}#{paddedMs}#{id}`, so a row can appear only in the `all` query
//    (`begins_with(SK, 'notification#')`) and its own category's query
//    (`begins_with(SK, 'notification#{category}#')`). Marking a `social` row read cannot change what
//    `list('patterns')` holds. This is why `keys.ts` exports NO prefix: `['me','notifications','list']`
//    would invalidate four entries no write touched.
//
//  * NO CROSS-SECTION INVALIDATION, and this absence IS AN ASSERTION (Req 5.6). Slice 5 REQUIRED
//    `collection/mutations.ts` to import `patternKeys`, because adding a pattern to a collection writes
//    `collection_count` on the pattern record. Notifications have no such foreign write: `markRead`
//    (`:625-654`) and `deleteNotification` (`:660-696`) touch exactly two records, BOTH under
//    `PK = user#{identityId}`. So this module imports no other section's key factory, and
//    `__tests__/mutations.test.tsx` seeds `patternKeys.detail('pat-P')` specifically to prove neither
//    write reaches it. The contrast with slice 5 is the point — "no cross-section invalidation" here is a
//    verified fact, not an oversight.
//
// NO OPTIMISTIC UPDATES, deliberately (Req 5.11). Both handlers `await` the counter update BEFORE
// responding, so by the time `onSuccess` runs the server value is already decremented and the
// invalidation-driven refetch reads the new number — there is no race to paper over. An optimistic patch
// would add a second, hand-maintained copy of the count, which is precisely what this slice removed. The
// visible difference from the pre-migration code is that the decrement appears after a round trip rather
// than in the same tick; the end state is identical.
//
// Both mutations reject with the ORIGINAL error object, untouched, so each surface's existing
// `console.error` catch behaves exactly as before and no new user-visible error text appears (Req 5.7).
// Mutations are not retried (React Query does not retry them by default), so a mark-read or a delete
// happens exactly once (Req 5.8).

import type { MarkNotificationReadResponse, NotificationCategory } from '@mrb91002/shared-types'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { deleteNotification, markNotificationRead } from '../../api/secure/notifications'
import { useAuth } from '../../context/AuthContext'

import { notificationKeys } from './keys'

/** A notification's write coordinates. `'all'` is excluded because it is a view, never a stored row. */
export type NotificationWriteTarget = {
  id: string
  category: Exclude<NotificationCategory, 'all'>
  created_at_ms: number
}

/**
 * Delete input — the write coordinates PLUS the row's `read` flag.
 *
 * `read` is here because it selects which invalidation matrix row applies (see below), and it comes from
 * the mutation VARIABLES rather than a hook argument because both callers delete an arbitrary row out of
 * a rendered list.
 */
export type DeleteNotificationInput = NotificationWriteTarget & { read: boolean }

/**
 * Mark ONE notification read. Callers: `NotificationCenter` (row click, action-button click) and
 * `NotificationBell` (row click, action-button click).
 *
 * `onSuccess` invalidates `list('all')` + `list(category)` + `unreadCount()` — three keys, and no others.
 * See the matrix in the module header for the handler writes each one corresponds to.
 */
export function useMarkNotificationReadMutation() {
  const queryClient = useQueryClient()
  const { idToken } = useAuth()

  return useMutation<MarkNotificationReadResponse, Error, NotificationWriteTarget>({
    mutationFn: ({ id, category, created_at_ms }) => {
      if (!idToken) throw new Error('Cannot mark a notification read while signed out')
      return markNotificationRead(id, category, created_at_ms, idToken)
    },
    onSuccess: (_result, { category }) => {
      // The row is in the 'all' list and in its OWN category's list — never in a sibling's.
      queryClient.invalidateQueries({ queryKey: notificationKeys.list('all') })
      queryClient.invalidateQueries({ queryKey: notificationKeys.list(category) })
      // The counter record moved (the row was unread — the caller gates on that).
      queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() })
    },
  })
}

/**
 * Delete ONE notification. Caller: `NotificationCenter` (the per-row 🗑️ control and the detail-panel
 * Delete button).
 *
 * `onSuccess` invalidates `list('all')` + `list(category)` ALWAYS, and `unreadCount()` **ONLY WHEN THE
 * ROW WAS UNREAD** — which is the load-bearing ANTI-CASE of this slice (Req 5.5).
 *
 * `lambda-secure/src/services/notifications.ts:682` decrements the counter only
 * `if (result.Attributes['read'] === false)`. Deleting an ALREADY-READ notification therefore cannot move
 * any count, so invalidating `unreadCount()` there would be a refetch with no cause — the same shape as
 * slice 5's "a 409 changed nothing, so do not invalidate its `detail`". **Do not "simplify" this into an
 * unconditional invalidation**: `__tests__/mutations.test.tsx` asserts the already-read leg leaves the
 * count entry untouched, and that assertion is mutation-checked.
 *
 * Residual staleness, bounded and named: if the client's `read` flag is itself stale (another tab marked
 * the row read), the branch can go the wrong way. Stale-unread → we invalidate → refetch → correct.
 * Stale-read → we skip → the count stays stale until the 30 s window expires. The pre-migration code
 * never re-read the count at all, so this is strictly better than today in both directions.
 *
 * NOTE it does NOT `removeQueries` the way slice 5's collection delete did. That decision hinged on the
 * caller NAVIGATING AWAY, so a return visit could repaint pre-delete content from cache. Here the caller
 * stays on `/notifications` and keeps rendering the same list entries, which must therefore keep their
 * data while the refetch is in flight — removing them would blank the list to its empty state and then
 * repopulate it, a flash the pre-migration code did not have.
 */
export function useDeleteNotificationMutation() {
  const queryClient = useQueryClient()
  const { idToken } = useAuth()

  return useMutation<void, Error, DeleteNotificationInput>({
    mutationFn: ({ id, category, created_at_ms }) => {
      if (!idToken) throw new Error('Cannot delete a notification while signed out')
      return deleteNotification(id, category, created_at_ms, idToken)
    },
    onSuccess: (_result, { category, read }) => {
      // The row left the 'all' list and its OWN category's list — never a sibling's.
      queryClient.invalidateQueries({ queryKey: notificationKeys.list('all') })
      queryClient.invalidateQueries({ queryKey: notificationKeys.list(category) })
      // ANTI-CASE: a READ row's deletion moves no counter (handler gates on `read === false`), so the
      // count entry is deliberately left alone. See the block comment above before changing this.
      if (!read) {
        queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() })
      }
    },
  })
}
